/**
 * DESIGN-FOUNDATION-3 — Motion tokens
 *
 * JS-accessible equivalents of tokens-v2.css duration/easing values.
 * Static fallbacks are the canonical values; runtime reader pulls from
 * computed styles when CSS custom properties are available.
 *
 * Source of truth: src/styles/tokens-v2.css §5 animation layer
 */

/** Duration values in seconds (matching --duration-{N} tokens) */
export const DURATION = {
  50:  0.05,
  100: 0.10,
  150: 0.15,
  200: 0.20,
  300: 0.30,
  500: 0.50,
} as const;

export type DurationKey = keyof typeof DURATION;

/** Easing curves (matching --ease-* tokens in tokens-v2.css) */
export const EASING = {
  linear:      [0, 0, 1, 1],
  easeOut:     [0, 0, 0.2, 1],
  easeIn:      [0.4, 0, 1, 1],
  easeInOut:   [0.4, 0, 0.2, 1],
  outQuint:    [0.22, 1, 0.36, 1],
  inQuint:     [0.64, 0, 0.78, 0],
  spring:      { type: 'spring' as const, stiffness: 400, damping: 30 },
  springBounce:{ type: 'spring' as const, stiffness: 300, damping: 20 },
} as const;

export type EasingKey = keyof typeof EASING;

/**
 * Read a CSS custom property value from :root at runtime.
 * Falls back to the static DURATION map if the property is not found.
 */
export function readCssDuration(property: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(property).trim();
  if (!raw) return fallback;
  // CSS value is in ms (e.g. "200ms") or s (e.g. "0.2s")
  if (raw.endsWith('ms')) return parseFloat(raw) / 1000;
  if (raw.endsWith('s'))  return parseFloat(raw);
  return fallback;
}
